// src/use-echarts.ts
import { init, use as echartsUse } from "echarts/core";
import { useEffect, useRef, useState } from "react";

// src/events.ts
var echartsEvents = {
  onClick: "click",
  onDoubleClick: "dblclick",
  onMouseDown: "mousedown",
  onMouseMove: "mousemove",
  onMouseOver: "mouseover",
  onMouseOut: "mouseout",
  onGlobalOut: "globalout",
  onContextMenu: "contextmenu",
  onHighlight: "highlight",
  onDownplay: "downplay",
  onSelectChanged: "selectchanged",
  onLegendSelectChanged: "legendselectchanged",
  onLegendSelected: "legendselected",
  onLegendUnselected: "legendunselected",
  onLegendInverseSelect: "legendinverseselect",
  onLegendScroll: "legendscroll",
  onDataZoom: "datazoom",
  onDataRangeSelected: "datarangeselected",
  onTimelineChanged: "timelinechanged",
  onTimelinePlayChanged: "timelineplaychanged",
  onRestore: "restore",
  onDataViewChanged: "dataviewchanged",
  onMagicTypeChanged: "magictypechanged",
  onGeoSelectChanged: "geoselectchanged",
  onGeoSelected: "geoselected",
  onGeoUnselected: "geounselected",
  onAxisAreaSelected: "axisareaselected",
  onBrush: "brush",
  onBrushEnd: "brushend",
  onBrushSelected: "brushselected",
  onGlobalCursorTaken: "globalcursortaken",
  onRendered: "rendered",
  onFinished: "finished"
};

// src/use-echarts.ts
async function getGlobalUse() {
  const all = [
    import("echarts/features"),
    import("echarts/charts"),
    import("echarts/components"),
    import("echarts/renderers")
  ];
  const promise = await Promise.all(
    all.map((m) => m.then((m2) => Object.values(m2)))
  );
  return promise.flat();
}
function useECharts({
  // Init
  devicePixelRatio,
  height,
  locale,
  pointerSize,
  renderer,
  theme,
  use,
  useCoarsePointer,
  useDirtyRect,
  width,
  // eChartsInstance
  group,
  // SetOption
  lazyUpdate,
  notMerge,
  replaceMerge,
  silent,
  transition,
  darkMode,
  media,
  options,
  stateAnimation,
  // Option
  angleAxis,
  animation,
  animationDelay,
  animationDelayUpdate,
  animationDuration,
  animationDurationUpdate,
  animationEasing,
  animationEasingUpdate,
  animationThreshold,
  aria,
  axisPointer,
  backgroundColor,
  blendMode,
  brush,
  calendar,
  color,
  dataZoom,
  dataset,
  geo,
  graphic,
  grid,
  hoverLayerThreshold,
  legend,
  parallel,
  parallelAxis,
  polar,
  progressive,
  progressiveThreshold,
  radar,
  radiusAxis,
  series,
  singleAxis,
  textStyle,
  timeline,
  title,
  toolbox,
  tooltip,
  useUTC,
  visualMap,
  xAxis,
  yAxis,
  // Events
  onAxisAreaSelected,
  onBrush,
  onBrushEnd,
  onBrushSelected,
  onClick,
  onContextMenu,
  onDataRangeSelected,
  onDataViewChanged,
  onDataZoom,
  onDoubleClick,
  onDownplay,
  onFinished,
  onGeoSelectChanged,
  onGeoSelected,
  onGeoUnselected,
  onGlobalCursorTaken,
  onGlobalOut,
  onHighlight,
  onLegendInverseSelect,
  onLegendScroll,
  onLegendSelectChanged,
  onLegendSelected,
  onLegendUnselected,
  onMagicTypeChanged,
  onMouseDown,
  onMouseMove,
  onMouseOut,
  onMouseOver,
  onRendered,
  onRestore,
  onSelectChanged,
  onTimelineChanged,
  onTimelinePlayChanged
}) {
  const containerRef = useRef();
  const echartsRef = useRef();
  const resizeObserverRef = useRef();
  const [started, setStarted] = useState(false);
  const echartsInstance = echartsRef.current;
  async function setContainerRef(node) {
    if (containerRef.current && echartsRef.current) return;
    containerRef.current = node;
    echartsRef.current = await startEcharts();
    resizeObserverRef.current = startResizeObserver();
    setStarted(true);
  }
  async function startEcharts() {
    if (!containerRef.current) return;
    const useOpts = use || await getGlobalUse();
    echartsUse(useOpts);
    return init(containerRef.current, theme, {
      devicePixelRatio,
      height,
      locale,
      pointerSize,
      renderer,
      useCoarsePointer,
      useDirtyRect,
      width
    });
  }
  function startResizeObserver() {
    const resizeObserver = new ResizeObserver(() => {
      echartsRef.current?.resize();
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return resizeObserver;
  }
  useEffect(() => {
    return () => echartsInstance?.dispose?.();
  }, []);
  useEffect(() => {
    if (!echartsInstance) return;
    if (group) echartsInstance.group = group;
  }, [group, started, echartsInstance]);
  useEffect(() => {
    if (!echartsInstance) return;
    echartsInstance.setOption(
      {
        series,
        useUTC,
        xAxis,
        yAxis,
        progressive,
        blendMode,
        hoverLayerThreshold,
        progressiveThreshold,
        animation,
        animationDelay,
        animationDelayUpdate,
        animationDuration,
        animationDurationUpdate,
        animationEasing,
        animationEasingUpdate,
        animationThreshold,
        ...angleAxis && { angleAxis },
        ...aria && { aria },
        ...axisPointer && { axisPointer },
        ...backgroundColor && { backgroundColor },
        ...brush && { brush },
        ...calendar && { calendar },
        ...color && { color },
        ...darkMode && { darkMode },
        ...dataset && { dataset },
        ...dataZoom && { dataZoom },
        ...geo && { geo },
        ...graphic && { graphic },
        ...grid && { grid },
        ...legend && { legend },
        ...media && { media },
        ...options && { options },
        ...parallel && { parallel },
        ...parallelAxis && { parallelAxis },
        ...polar && { polar },
        ...radar && { radar },
        ...radiusAxis && { radiusAxis },
        ...series && { series },
        ...singleAxis && { singleAxis },
        ...stateAnimation && { stateAnimation },
        ...textStyle && { textStyle },
        ...timeline && { timeline },
        ...title && { title },
        ...toolbox && { toolbox },
        ...tooltip && { tooltip },
        ...useUTC && { useUTC },
        ...visualMap && { visualMap },
        ...xAxis && { xAxis },
        ...yAxis && { yAxis }
      },
      {
        lazyUpdate,
        notMerge,
        replaceMerge,
        silent,
        transition
      }
    );
  }, [
    angleAxis,
    animation,
    animationDelay,
    animationDelayUpdate,
    animationDuration,
    animationDurationUpdate,
    animationEasing,
    animationEasingUpdate,
    animationThreshold,
    aria,
    axisPointer,
    backgroundColor,
    blendMode,
    brush,
    calendar,
    color,
    darkMode,
    dataset,
    dataZoom,
    geo,
    graphic,
    grid,
    hoverLayerThreshold,
    legend,
    media,
    options,
    parallel,
    parallelAxis,
    polar,
    progressive,
    progressiveThreshold,
    radar,
    radiusAxis,
    series,
    singleAxis,
    stateAnimation,
    textStyle,
    timeline,
    title,
    toolbox,
    tooltip,
    useUTC,
    visualMap,
    xAxis,
    yAxis,
    //
    lazyUpdate,
    notMerge,
    replaceMerge,
    silent,
    transition,
    //
    started,
    echartsInstance
  ]);
  useEffect(() => {
    if (!echartsInstance) return;
    if (onAxisAreaSelected) {
      echartsInstance.off(echartsEvents.onAxisAreaSelected);
      echartsInstance.on(echartsEvents.onAxisAreaSelected, onAxisAreaSelected);
    }
    if (onBrush) {
      echartsInstance.off(echartsEvents.onBrush);
      echartsInstance.on(echartsEvents.onBrush, onBrush);
    }
    if (onBrushEnd) {
      echartsInstance.off(echartsEvents.onBrushEnd);
      echartsInstance.on(echartsEvents.onBrushEnd, onBrushEnd);
    }
    if (onBrushSelected) {
      echartsInstance.off(echartsEvents.onBrushSelected);
      echartsInstance.on(echartsEvents.onBrushSelected, onBrushSelected);
    }
    if (onClick) {
      echartsInstance.off(echartsEvents.onClick);
      echartsInstance.on(echartsEvents.onClick, onClick);
    }
    if (onContextMenu) {
      echartsInstance.off(echartsEvents.onContextMenu);
      echartsInstance.on(echartsEvents.onContextMenu, onContextMenu);
    }
    if (onDataRangeSelected) {
      echartsInstance.off(echartsEvents.onDataRangeSelected);
      echartsInstance.on(echartsEvents.onDataRangeSelected, onDataRangeSelected);
    }
    if (onDataViewChanged) {
      echartsInstance.off(echartsEvents.onDataViewChanged);
      echartsInstance.on(echartsEvents.onDataViewChanged, onDataViewChanged);
    }
    if (onDataZoom) {
      echartsInstance.off(echartsEvents.onDataZoom);
      echartsInstance.on(echartsEvents.onDataZoom, onDataZoom);
    }
    if (onDoubleClick) {
      echartsInstance.off(echartsEvents.onDoubleClick);
      echartsInstance.on(echartsEvents.onDoubleClick, onDoubleClick);
    }
    if (onDownplay) {
      echartsInstance.off(echartsEvents.onDownplay);
      echartsInstance.on(echartsEvents.onDownplay, onDownplay);
    }
    if (onFinished) {
      echartsInstance.off(echartsEvents.onFinished);
      echartsInstance.on(echartsEvents.onFinished, onFinished);
    }
    if (onGeoSelectChanged) {
      echartsInstance.off(echartsEvents.onGeoSelectChanged);
      echartsInstance.on(echartsEvents.onGeoSelectChanged, onGeoSelectChanged);
    }
    if (onGeoSelected) {
      echartsInstance.off(echartsEvents.onGeoSelected);
      echartsInstance.on(echartsEvents.onGeoSelected, onGeoSelected);
    }
    if (onGeoUnselected) {
      echartsInstance.off(echartsEvents.onGeoUnselected);
      echartsInstance.on(echartsEvents.onGeoUnselected, onGeoUnselected);
    }
    if (onGlobalCursorTaken) {
      echartsInstance.off(echartsEvents.onGlobalCursorTaken);
      echartsInstance.on(echartsEvents.onGlobalCursorTaken, onGlobalCursorTaken);
    }
    if (onGlobalOut) {
      echartsInstance.off(echartsEvents.onGlobalOut);
      echartsInstance.on(echartsEvents.onGlobalOut, onGlobalOut);
    }
    if (onHighlight) {
      echartsInstance.off(echartsEvents.onHighlight);
      echartsInstance.on(echartsEvents.onHighlight, onHighlight);
    }
    if (onLegendInverseSelect) {
      echartsInstance.off(echartsEvents.onLegendInverseSelect);
      echartsInstance.on(echartsEvents.onLegendInverseSelect, onLegendInverseSelect);
    }
    if (onLegendScroll) {
      echartsInstance.off(echartsEvents.onLegendScroll);
      echartsInstance.on(echartsEvents.onLegendScroll, onLegendScroll);
    }
    if (onLegendScroll) {
      echartsInstance.off(echartsEvents.onLegendScroll);
      echartsInstance.on(echartsEvents.onLegendScroll, onLegendScroll);
    }
    if (onLegendSelectChanged) {
      echartsInstance.off(echartsEvents.onLegendSelectChanged);
      echartsInstance.on(echartsEvents.onLegendSelectChanged, onLegendSelectChanged);
    }
    if (onLegendSelected) {
      echartsInstance.off(echartsEvents.onLegendSelected);
      echartsInstance.on(echartsEvents.onLegendSelected, onLegendSelected);
    }
    if (onLegendUnselected) {
      echartsInstance.off(echartsEvents.onLegendUnselected);
      echartsInstance.on(echartsEvents.onLegendUnselected, onLegendUnselected);
    }
    if (onMagicTypeChanged) {
      echartsInstance.off(echartsEvents.onMagicTypeChanged);
      echartsInstance.on(echartsEvents.onMagicTypeChanged, onMagicTypeChanged);
    }
    if (onMouseDown) {
      echartsInstance.off(echartsEvents.onMouseDown);
      echartsInstance.on(echartsEvents.onMouseDown, onMouseDown);
    }
    if (onMouseMove) {
      echartsInstance.off(echartsEvents.onMouseMove);
      echartsInstance.on(echartsEvents.onMouseMove, onMouseMove);
    }
    if (onMouseOut) {
      echartsInstance.off(echartsEvents.onMouseOut);
      echartsInstance.on(echartsEvents.onMouseOut, onMouseOut);
    }
    if (onMouseOver) {
      echartsInstance.off(echartsEvents.onMouseOver);
      echartsInstance.on(echartsEvents.onMouseOver, onMouseOver);
    }
    if (onRendered) {
      echartsInstance.off(echartsEvents.onRendered);
      echartsInstance.on(echartsEvents.onRendered, onRendered);
    }
    if (onRestore) {
      echartsInstance.off(echartsEvents.onRestore);
      echartsInstance.on(echartsEvents.onRestore, onRestore);
    }
    if (onSelectChanged) {
      echartsInstance.off(echartsEvents.onSelectChanged);
      echartsInstance.on(echartsEvents.onSelectChanged, onSelectChanged);
    }
    if (onTimelineChanged) {
      echartsInstance.off(echartsEvents.onTimelineChanged);
      echartsInstance.on(echartsEvents.onTimelineChanged, onTimelineChanged);
    }
    if (onTimelinePlayChanged) {
      echartsInstance.off(echartsEvents.onTimelinePlayChanged);
      echartsInstance.on(echartsEvents.onTimelinePlayChanged, onTimelinePlayChanged);
    }
  }, [
    onAxisAreaSelected,
    onBrush,
    onBrushEnd,
    onBrushSelected,
    onClick,
    onContextMenu,
    onDataRangeSelected,
    onDataViewChanged,
    onDataZoom,
    onDoubleClick,
    onDownplay,
    onFinished,
    onGeoSelectChanged,
    onGeoSelected,
    onGeoUnselected,
    onGlobalCursorTaken,
    onGlobalOut,
    onHighlight,
    onLegendInverseSelect,
    onLegendScroll,
    onLegendSelectChanged,
    onLegendSelected,
    onLegendUnselected,
    onMagicTypeChanged,
    onMouseDown,
    onMouseMove,
    onMouseOut,
    onMouseOver,
    onRendered,
    onRestore,
    onSelectChanged,
    onTimelineChanged,
    onTimelinePlayChanged,
    started
  ]);
  return [setContainerRef, echartsRef.current];
}

// src/use-connect.ts
import { connect, disconnect } from "echarts/core";
import { useState as useState2 } from "react";
var useConnect = (group) => {
  const [isConnected, setIsConnected] = useState2(false);
  function onChange(connected) {
    if (connected) {
      connect(group);
    } else {
      disconnect(group);
    }
    setIsConnected(connected);
  }
  return [isConnected, onChange];
};

// src/echart.tsx
import { jsx } from "react/jsx-runtime";
var EChart = ({
  // Init
  devicePixelRatio,
  height,
  locale,
  pointerSize,
  renderer,
  theme,
  use,
  useCoarsePointer,
  useDirtyRect,
  width,
  // eChartsInstance
  group,
  // SetOption
  lazyUpdate,
  notMerge,
  replaceMerge,
  silent,
  transition,
  darkMode,
  media,
  options,
  stateAnimation,
  // Option
  angleAxis,
  animation,
  animationDelay,
  animationDelayUpdate,
  animationDuration,
  animationDurationUpdate,
  animationEasing,
  animationEasingUpdate,
  animationThreshold,
  aria,
  axisPointer,
  backgroundColor,
  blendMode,
  brush,
  calendar,
  color,
  dataZoom,
  dataset,
  geo,
  graphic,
  grid,
  hoverLayerThreshold,
  legend,
  parallel,
  parallelAxis,
  polar,
  progressive,
  progressiveThreshold,
  radar,
  radiusAxis,
  series,
  singleAxis,
  textStyle,
  timeline,
  title,
  toolbox,
  tooltip,
  useUTC,
  visualMap,
  xAxis,
  yAxis,
  // Events
  onAxisAreaSelected,
  onBrush,
  onBrushEnd,
  onBrushSelected,
  onClick,
  onContextMenu,
  onDataRangeSelected,
  onDataViewChanged,
  onDataZoom,
  onDoubleClick,
  onDownplay,
  onFinished,
  onGeoSelectChanged,
  onGeoSelected,
  onGeoUnselected,
  onGlobalCursorTaken,
  onGlobalOut,
  onHighlight,
  onLegendInverseSelect,
  onLegendScroll,
  onLegendSelectChanged,
  onLegendSelected,
  onLegendUnselected,
  onMagicTypeChanged,
  onMouseDown,
  onMouseMove,
  onMouseOut,
  onMouseOver,
  onRendered,
  onRestore,
  onSelectChanged,
  onTimelineChanged,
  onTimelinePlayChanged,
  ...rest
}) => {
  const [ref] = useECharts({
    // Init
    devicePixelRatio,
    height,
    locale,
    pointerSize,
    renderer,
    theme,
    use,
    useCoarsePointer,
    useDirtyRect,
    width,
    // eChartsInstance
    group,
    // SetOption
    lazyUpdate,
    notMerge,
    replaceMerge,
    silent,
    transition,
    darkMode,
    media,
    options,
    stateAnimation,
    // Option
    angleAxis,
    animation,
    animationDelay,
    animationDelayUpdate,
    animationDuration,
    animationDurationUpdate,
    animationEasing,
    animationEasingUpdate,
    animationThreshold,
    aria,
    axisPointer,
    backgroundColor,
    blendMode,
    brush,
    calendar,
    color,
    dataZoom,
    dataset,
    geo,
    graphic,
    grid,
    hoverLayerThreshold,
    legend,
    parallel,
    parallelAxis,
    polar,
    progressive,
    progressiveThreshold,
    radar,
    radiusAxis,
    series,
    singleAxis,
    textStyle,
    timeline,
    title,
    toolbox,
    tooltip,
    useUTC,
    visualMap,
    xAxis,
    yAxis,
    // Events
    onAxisAreaSelected,
    onBrush,
    onBrushEnd,
    onBrushSelected,
    onClick,
    onContextMenu,
    onDataRangeSelected,
    onDataViewChanged,
    onDataZoom,
    onDoubleClick,
    onDownplay,
    onFinished,
    onGeoSelectChanged,
    onGeoSelected,
    onGeoUnselected,
    onGlobalCursorTaken,
    onGlobalOut,
    onHighlight,
    onLegendInverseSelect,
    onLegendScroll,
    onLegendSelectChanged,
    onLegendSelected,
    onLegendUnselected,
    onMagicTypeChanged,
    onMouseDown,
    onMouseMove,
    onMouseOut,
    onMouseOver,
    onRendered,
    onRestore,
    onSelectChanged,
    onTimelineChanged,
    onTimelinePlayChanged
  });
  return /* @__PURE__ */ jsx("div", { ...rest, ref });
};
export {
  EChart,
  useConnect,
  useECharts
};
//# sourceMappingURL=index.mjs.map